import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/utils";

/**
 * Badge — base for status indicators (sold-out, retired, pending, approved)
 * AND for the bespoke <TierBadge> component (Story 3.5) which wraps this.
 */
const badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium font-body transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-brand-forest-900 text-brand-paper-100",
        secondary:
          "border-transparent bg-brand-forest-50 text-brand-forest-900",
        destructive:
          "border-transparent bg-state-error-fill text-state-error-fg",
        success:
          "border-transparent bg-state-success-fill text-state-success-fg",
        warning:
          "border-transparent bg-state-warning-fill text-state-warning-fg",
        outline: "text-foreground",
      },
    },
    defaultVariants: { variant: "default" },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

// <span>, not <div> (shadcn default): badges are inline by nature and
// several call sites nest them in <p> — a div there is invalid HTML
// (React's validateDOMNesting warning). inline-flex renders identically.
export function Badge({ className, variant, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ variant }), className)} {...props} />;
}

export { badgeVariants };
